import React, { useMemo } from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { Feature, FeatureCollection, Polygon } from 'geojson'
import { localizedStrings } from '@core/strings'
import { latLngToBNG, getPolygonVertices } from '@modules/geolocation'
import useFeatureFlags from '@core/providers/useFeatureFlags'

const HIGHLIGHT_BACKGROUND = '#EDE5FB'

type PolygonCoordinatesListProps = {
  featurePolygon?: Feature<Polygon> | FeatureCollection<Polygon>
  verticesPerLine?: 'one' | 'all'
  highlightedIndex?: number | null
  // A specific vertex to highlight (1-based polygon index, 0-based vertex index
  // within that polygon's flattened coordinate list).
  highlightedVertex?: { polygonIndex: number; vertexIndex: number } | null
}

type PolygonRow = {
  index: number
  vertices: Array<{ easting: number; northing: number }>
}

const collectPolygonFeatures = (
  featurePolygon: Feature<Polygon> | FeatureCollection<Polygon>,
): Feature<Polygon>[] => {
  if ('features' in featurePolygon) {
    return featurePolygon.features
  }
  return [featurePolygon]
}

export const PolygonEastingNorthingList: React.FC<
  PolygonCoordinatesListProps
> = ({
  featurePolygon,
  verticesPerLine = 'all',
  highlightedIndex,
  highlightedVertex,
}) => {
  const { flags } = useFeatureFlags(['british_grid_eastings_northings'])

  const rows = useMemo<PolygonRow[]>(() => {
    if (!featurePolygon) return []

    return collectPolygonFeatures(featurePolygon).map((feature, polygonIdx) => {
      const vertices = getPolygonVertices(feature).map(([lng, lat]) =>
        latLngToBNG(lat, lng),
      )

      return { index: polygonIdx + 1, vertices }
    })
  }, [featurePolygon])

  if (!flags.british_grid_eastings_northings) return null

  if (rows.length === 0 || rows.every((r) => r.vertices.length === 0))
    return null

  const formatCoord = (n: number) =>
    String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

  const isVertexHighlighted = (rowIndex: number, vertexIndex: number) =>
    highlightedVertex?.polygonIndex === rowIndex &&
    highlightedVertex?.vertexIndex === vertexIndex

  return (
    <Box sx={{ pt: 2 }}>
      <Text fontSize={14} fontWeight={500} sx={{ pb: 1 }}>
        {localizedStrings.polygonCoordinates}
      </Text>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {rows.map((row, i) => (
          <Flex
            key={row.index}
            sx={{
              px: 2,
              py: 1,
              alignItems: 'flex-start',
              ...(i < rows.length - 1 && {
                borderBottom: 1,
                borderColor: 'divider',
              }),
              ...(highlightedIndex === row.index && {
                bgcolor: HIGHLIGHT_BACKGROUND,
              }),
            }}
          >
            <Text
              fontSize={13}
              fontWeight={600}
              sx={{ flex: '0 0 90px', color: 'text.secondary' }}
            >
              {`${localizedStrings.polygon} ${row.index}`}
            </Text>
            {verticesPerLine === 'one' ? (
              <Box sx={{ flex: 1 }}>
                {row.vertices.map((v, vi) => (
                  <Text
                    key={vi}
                    fontSize={11}
                    sx={
                      isVertexHighlighted(row.index, vi)
                        ? {
                            bgcolor: HIGHLIGHT_BACKGROUND,
                            fontWeight: 600,
                            borderRadius: 0.5,
                            textDecoration: 'underline',
                          }
                        : undefined
                    }
                  >
                    {`${formatCoord(v.easting)}, ${formatCoord(v.northing)}`}
                  </Text>
                ))}
              </Box>
            ) : (
              // Plain Box (a <div>) rather than <Text>: the fundamentals Text
              // wraps its children in a MUI Tooltip with `title={children}`,
              // which misbehaves when children is an element array rather than
              // a string. We need per-vertex spans here to highlight one coord.
              <Box
                sx={{
                  flex: 1,
                  fontSize: '11px',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}
              >
                {row.vertices.map((v, vi) => {
                  const label = `${formatCoord(v.easting)}, ${formatCoord(
                    v.northing,
                  )}`

                  return (
                    <React.Fragment key={vi}>
                      {vi > 0 && '  •  '}
                      {isVertexHighlighted(row.index, vi) ? (
                        <Box
                          component='span'
                          sx={{
                            backgroundColor: HIGHLIGHT_BACKGROUND,
                            fontWeight: 600,
                            borderRadius: '2px',
                            px: '2px',
                            textDecoration: 'underline',
                          }}
                        >
                          {label}
                        </Box>
                      ) : (
                        label
                      )}
                    </React.Fragment>
                  )
                })}
              </Box>
            )}
          </Flex>
        ))}
      </Box>
    </Box>
  )
}
